import { HelpCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { SCENARIO_VARIABLES } from '@super/shared-types'
import type { ScenarioType } from '@super/shared-types'
import type { FormValues } from './agent-detail.schema'

type VariablesSidePanelProps = {
  triggerType?: FormValues['triggerType']
  isOpen: boolean
  onClose: () => void
  channels?: string[]
  resolvedScenarioType?: ScenarioType
  /** Declared variables of a selected custom Trigger (all optional). */
  customVariables?: string[]
}

const TRIGGER_SCENARIO_MAP: Partial<Record<string, ScenarioType>> = {
  LEAD_WEBHOOK: 'LEASING',
}

const COLLECTIONS_VARIABLES = [
  { name: 'firstName', description: 'Payer first name' },
  { name: 'lastName', description: 'Payer last name' },
  { name: 'fullName', description: 'Payer full name' },
  { name: 'phone', description: 'Payer phone number' },
  { name: 'email', description: 'Payer email address' },
  { name: 'property', description: 'Property name' },
  { name: 'address', description: 'Property address' },
  { name: 'unitNumber', description: 'Unit number' },
  { name: 'amountOwed', description: 'Amount owed (e.g. 1450 for $1,450)' },
  { name: 'dueDate', description: 'Payment due date (YYYY-MM-DD)' },
  { name: 'daysOverdue', description: 'Days past due' },
]

function VariableItem({
  name,
  description,
}: {
  name: string
  description: string
}) {
  return (
    <div className="flex items-start gap-2 rounded-md px-2 py-1">
      <div className="min-w-0 flex-1">
        <code className="text-xs font-medium">{'{{' + name + '}}'}</code>
        <p className="text-[11px] text-muted-foreground truncate">
          {description}
        </p>
      </div>
    </div>
  )
}

const GENERAL_VARIABLES = [
  { name: 'companyName', description: 'Your company name (auto-filled)' },
  { name: 'agentName', description: 'Agent name (auto-filled)' },
]

const EMAIL_VARIABLES = [
  { name: 'firstName', description: 'Lead first name' },
  { name: 'lastName', description: 'Lead last name' },
  { name: 'email', description: 'Lead email address' },
  { name: 'phone', description: 'Lead phone number' },
  { name: 'property', description: 'Property name' },
  { name: 'comments', description: 'Lead comments or questions' },
  { name: 'listingURL', description: 'URL to listing or showing calendar' },
]

const SCENARIO_LABELS: Record<string, string> = {
  EMERGENCY: 'Emergency variables',
  LEASING: 'Leasing variables',
  MAINTENANCE: 'Maintenance variables',
}

export function VariablesSidePanel({
  triggerType,
  isOpen,
  onClose,
  channels = [],
  resolvedScenarioType,
  customVariables = [],
}: VariablesSidePanelProps) {
  const hasEmail = triggerType !== 'INBOUND_CALL' && channels.includes('EMAIL')
  // A selected Trigger carries its own variables (the source of truth). The
  // hardcoded scenario/collections lists below are only a fallback for legacy
  // agents still on the built-in triggerType enum (no Trigger row selected).
  const hasTriggerVariables = customVariables.length > 0
  const scenarioType =
    resolvedScenarioType ??
    (triggerType ? TRIGGER_SCENARIO_MAP[triggerType] : undefined)
  const scenarioVars =
    !hasTriggerVariables && scenarioType ? SCENARIO_VARIABLES[scenarioType] : []
  const isCollections =
    !hasTriggerVariables && triggerType === 'PAYMENT_OVERDUE_WEBHOOK'
  if (!isOpen) return null

  return (
    <div className="sticky top-20 self-start w-72 shrink-0 border bg-muted/30 flex flex-col max-h-[calc(100vh-10rem)] rounded-lg overflow-hidden">
      <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Variables</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                Add {'{{variable}}'} to greeting, prompt, email subject, or SMS
                to use template variables within your prompts. To apply, start
                typing "{'{{'}" and the available variables will be suggested.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onClose}
          aria-label="Close panel"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 min-h-0 space-y-4">
        <section>
          <h3 className="px-1 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            General
          </h3>
          <div className="mt-1 space-y-1">
            {GENERAL_VARIABLES.map(v => (
              <VariableItem
                key={v.name}
                name={v.name}
                description={v.description}
              />
            ))}
          </div>
        </section>
        {customVariables.length > 0 && (
          <section>
            <h3 className="px-1 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Trigger variables
            </h3>
            <div className="mt-1 space-y-1">
              {customVariables.map(name => (
                <VariableItem
                  key={`trigger-${name}`}
                  name={name}
                  description="From the selected trigger"
                />
              ))}
            </div>
          </section>
        )}
        {scenarioVars.length > 0 && (
          <section>
            <h3 className="px-1 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {SCENARIO_LABELS[scenarioType!] ?? scenarioType}
            </h3>
            <div className="mt-1 space-y-1">
              {scenarioVars.map(v => (
                <VariableItem
                  key={v.name}
                  name={v.name}
                  description={v.description}
                />
              ))}
            </div>
          </section>
        )}
        {isCollections && (
          <section>
            <h3 className="px-1 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Collections variables
            </h3>
            <div className="mt-1 space-y-1">
              {COLLECTIONS_VARIABLES.map(v => (
                <VariableItem
                  key={`collections-${v.name}`}
                  name={v.name}
                  description={v.description}
                />
              ))}
            </div>
          </section>
        )}
        {hasEmail && (
          <section>
            <h3 className="px-1 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Email variables
            </h3>
            <div className="mt-1 space-y-1">
              {EMAIL_VARIABLES.map(v => (
                <VariableItem
                  key={`email-${v.name}`}
                  name={v.name}
                  description={v.description}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
